import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, Key, Phone, Building2, Save, RefreshCw, 
  Eye, EyeOff, CheckCircle, AlertCircle, Info, ExternalLink,
  Webhook, Shield, Send, ShieldCheck, Power, PowerOff
} from 'lucide-react';
import { whatsappAPI } from '../../services/api';

/**
 * WhatsApp Settings Page
 * Allows admins to configure WhatsApp Cloud API credentials
 * - Access Token
 * - Phone Number ID
 * - Business Account ID
 * - Webhook Verify Token
 */
const WhatsAppSettings = () => {
  const [settings, setSettings] = useState({
    accessToken: '',
    phoneNumberId: '',
    businessAccountId: '',
    webhookVerifyToken: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [message, setMessage] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState('');
  // Phase 13.1 — message type / template / media fields for the test sender.
  const [testType, setTestType] = useState('template');
  const [testTemplateName, setTestTemplateName] = useState('hello_world');
  const [testTemplateLang, setTestTemplateLang] = useState('en_US');
  const [testText, setTestText] = useState('WhatsApp connection successful');
  const [testMediaUrl, setTestMediaUrl] = useState('');
  const [testMediaType, setTestMediaType] = useState('image');
  const [testMediaCaption, setTestMediaCaption] = useState('');
  const [sendingMetaTest, setSendingMetaTest] = useState(false);
  // WhatsApp Integration global enable/disable toggle
  const [integrationEnabled, setIntegrationEnabled] = useState(true);
  const [toggling, setToggling] = useState(false);
  // iter225 — backend workflow cutoff + per-template toggles
  const [workflowStartDate, setWorkflowStartDate] = useState('');
  const [workflowToggles, setWorkflowToggles] = useState({});
  const [workflowCatalog, setWorkflowCatalog] = useState([]);
  const [savingWorkflows, setSavingWorkflows] = useState(false);

  useEffect(() => {
    loadSettings();
    loadWorkflowSettings();
  }, []);

  const loadWorkflowSettings = async () => {
    try {
      const res = await whatsappAPI.getWorkflowSettings();
      const d = res.data || {};
      setWorkflowStartDate(d.workflow_start_date || '');
      setWorkflowCatalog(d.workflows || []);
      const t = {};
      (d.workflows || []).forEach(w => { t[w.key] = w.enabled !== false; });
      setWorkflowToggles(t);
    } catch (err) {
      console.error('Error loading workflow settings:', err);
    }
  };

  const handleSaveWorkflows = async () => {
    try {
      setSavingWorkflows(true);
      await whatsappAPI.updateWorkflowSettings({
        workflow_start_date: workflowStartDate || '',
        workflow_toggles: workflowToggles,
      });
      setMessage({ type: 'success', text: 'Workflow controls saved.' });
    } catch (err) {
      setMessage({ type: 'error', text: err?.response?.data?.detail || 'Failed to save workflow controls.' });
    } finally {
      setSavingWorkflows(false);
    }
  };

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await whatsappAPI.getSettings();
      if (response.data) {
        setSettings({
          accessToken: response.data.accessToken || '',
          phoneNumberId: response.data.phoneNumberId || '',
          businessAccountId: response.data.businessAccountId || '',
          webhookVerifyToken: response.data.webhookVerifyToken || ''
        });
        setIntegrationEnabled(response.data.whatsapp_enabled !== false);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setMessage({ type: 'error', text: 'Failed to load WhatsApp settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      
      const updateData = {};
      if (settings.accessToken && !settings.accessToken.startsWith('***')) {
        updateData.accessToken = settings.accessToken;
      }
      if (settings.phoneNumberId) {
        updateData.phoneNumberId = settings.phoneNumberId;
      }
      if (settings.businessAccountId) {
        updateData.businessAccountId = settings.businessAccountId;
      }
      if (settings.webhookVerifyToken) {
        updateData.webhookVerifyToken = settings.webhookVerifyToken;
      }
      
      await whatsappAPI.updateSettings(updateData);
      setMessage({ type: 'success', text: 'WhatsApp settings saved successfully!' });
      
      // Reload settings to get masked token
      await loadSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Failed to save WhatsApp settings' });
    } finally {
      setSaving(false);
    }
  };

  const webhookUrl = `${window.location.origin}/api/whatsapp/webhook`;

  const handleToggleIntegration = async () => {
    const next = !integrationEnabled;
    setToggling(true);
    setMessage(null);
    try {
      await whatsappAPI.toggleIntegration(next);
      setIntegrationEnabled(next);
      setMessage({
        type: 'success',
        text: next
          ? 'WhatsApp Integration enabled. Outgoing messages will resume immediately.'
          : 'WhatsApp Integration disabled. All outgoing WhatsApp messages are paused. Credentials are preserved.',
      });
    } catch (error) {
      console.error('Error toggling integration:', error);
      setMessage({ type: 'error', text: 'Failed to update WhatsApp Integration status' });
    } finally {
      setToggling(false);
    }
  };

  const authHeader = () => {
    const t = localStorage.getItem('token') || localStorage.getItem('access_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const verifyCredentials = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/settings/verify`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      setVerifyResult(data);
    } catch (e) {
      setVerifyResult({ ok: false, category: 'network', error: e.message });
    } finally {
      setVerifying(false);
    }
  };

  const sendTest = async () => {
    if (!testTo || testTo.replace(/\D/g, '').length < 10) {
      setTestResult({ success: false, error: 'Enter a valid WA customer number (e.g. 919999999999) in the field above.' });
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const body = {
        to: testTo,
        type: testType,
      };
      if (testType === 'text') {
        body.body = testText || 'WhatsApp connection successful';
      } else if (testType === 'template') {
        body.template_name = testTemplateName || 'hello_world';
        body.template_lang = testTemplateLang || 'en_US';
      } else if (testType === 'media') {
        body.media_url = testMediaUrl;
        body.media_type = testMediaType || 'image';
        if (testMediaCaption) body.media_caption = testMediaCaption;
      }
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/settings/send-test`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      setTestResult(await res.json());
    } catch (e) {
      setTestResult({ success: false, error: e.message });
    } finally {
      setTesting(false);
    }
  };

  // Phase 13.1 — exact Meta Dashboard payload (template hello_world / en_US).
  const sendMetaTestPayload = async () => {
    if (!testTo || testTo.replace(/\D/g, '').length < 10) {
      setTestResult({ success: false, error: 'Enter the verified Meta test recipient number first (digits only, e.g. 919999999999).' });
      return;
    }
    setSendingMetaTest(true);
    setTestResult(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/settings/send-test`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: testTo,
          type: 'template',
          template_name: 'hello_world',
          template_lang: 'en_US',
        }),
      });
      setTestResult(await res.json());
    } catch (e) {
      setTestResult({ success: false, error: e.message });
    } finally {
      setSendingMetaTest(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-teal-600" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="whatsapp-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-xl">
              <MessageSquare className="text-green-600" size={24} />
            </div>
            WhatsApp Settings
          </h1>
          <p className="text-gray-500 mt-1">
            Configure WhatsApp Cloud API credentials for messaging
          </p>
        </div>
      </div>

      {/* WhatsApp Integration ON/OFF — always-visible status card at the very top */}
      <div
        className={`rounded-xl border p-6 ${integrationEnabled ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
        data-testid="whatsapp-integration-card"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-xl ${integrationEnabled ? 'bg-green-100' : 'bg-red-100'}`}>
              {integrationEnabled
                ? <Power className="text-green-600" size={24} />
                : <PowerOff className="text-red-600" size={24} />}
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-lg font-semibold text-gray-900">WhatsApp Integration</h2>
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                    integrationEnabled ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                  }`}
                  data-testid="whatsapp-integration-badge"
                >
                  <span className={`w-2 h-2 rounded-full ${integrationEnabled ? 'bg-green-200' : 'bg-red-200'}`} />
                  {integrationEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-1 max-w-2xl">
                {integrationEnabled
                  ? 'All outgoing WhatsApp communication (workflows, OTPs, reminders, manual tests) is active. Turn this off to pause every outgoing WhatsApp message without deleting any saved credentials or templates.'
                  : 'All outgoing WhatsApp communication is currently paused. No WhatsApp API requests are made. Your credentials, IDs and templates remain saved and editable.'}
              </p>
            </div>
          </div>

          {/* Toggle switch */}
          <button
            type="button"
            role="switch"
            aria-checked={integrationEnabled}
            onClick={handleToggleIntegration}
            disabled={toggling}
            className={`relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed ${
              integrationEnabled ? 'bg-green-600' : 'bg-gray-300'
            }`}
            data-testid="whatsapp-integration-toggle"
          >
            <span
              className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${
                integrationEnabled ? 'translate-x-7' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {!integrationEnabled && (
          <div
            className="mt-4 flex items-start gap-3 rounded-lg bg-amber-100 border border-amber-300 p-3"
            data-testid="whatsapp-disabled-warning"
          >
            <AlertCircle className="text-amber-600 mt-0.5 shrink-0" size={18} />
            <p className="text-sm text-amber-800">
              WhatsApp is disabled. Outgoing messages, workflows, OTPs and the "Send Test" actions
              are blocked until you re-enable the integration. Configuration below stays editable.
            </p>
          </div>
        )}
      </div>

      {message && (
        <div className={`p-4 rounded-xl flex items-center gap-3 ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-700 border border-green-200' 
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          {message.text}
        </div>
      )}

      {/* iter225 — Backend Workflow Controls: cutoff date + per-template toggles.
          Admin-Panel Guest Journey messages, OTP, and manual sends are NOT
          affected — this only gates the CRM's auto backend workflow triggers. */}
      <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="wa-workflow-controls-card">
        <div className="flex items-start justify-between mb-4 gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Backend Workflow Controls</h2>
            <p className="text-sm text-gray-600 mt-1">
              Choose the cutoff date and which auto-triggered CRM workflow templates are active.
              Guest-Journey (Admin Panel) messages and manual sends are not affected.
            </p>
          </div>
          <button
            onClick={handleSaveWorkflows}
            disabled={savingWorkflows}
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-60"
            data-testid="wa-workflow-save-btn"
          >
            {savingWorkflows ? 'Saving…' : 'Save Workflow Controls'}
          </button>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-800 mb-1.5">
            WhatsApp Workflow Start Date
          </label>
          <input
            type="date"
            value={workflowStartDate || ''}
            onChange={(e) => setWorkflowStartDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            data-testid="wa-workflow-start-date-input"
          />
          <p className="text-xs text-gray-500 mt-1.5">
            Accounts created BEFORE this date will not receive any backend workflow WhatsApp messages.
            Leave blank to send workflows for all accounts.
          </p>
        </div>

        <div>
          <p className="text-sm font-medium text-gray-800 mb-2">Backend Workflow Templates</p>
          <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg" data-testid="wa-workflow-toggles">
            {workflowCatalog.map(w => (
              <div key={w.key} className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-gray-800">{w.label}</p>
                  <p className="text-xs text-gray-500 font-mono">{w.key}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={workflowToggles[w.key] !== false}
                    onChange={(e) => setWorkflowToggles(t => ({ ...t, [w.key]: e.target.checked }))}
                    className="sr-only peer"
                    data-testid={`wa-workflow-toggle-${w.key}`}
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-checked:bg-emerald-600 rounded-full transition-colors relative">
                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${workflowToggles[w.key] !== false ? 'translate-x-5' : ''}`} />
                  </div>
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Info className="text-blue-600 mt-0.5" size={20} />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">How to get WhatsApp Cloud API credentials:</p>
            <ol className="list-decimal ml-4 space-y-1">
              <li>Go to <a href="https://developers.facebook.com/" target="_blank" rel="noopener noreferrer" className="underline">Meta for Developers</a></li>
              <li>Create or select your WhatsApp Business App</li>
              <li>Navigate to WhatsApp &gt; API Setup</li>
              <li>Copy the Phone Number ID and generate an Access Token</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Settings Form */}
      <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
        {/* Access Token */}
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-amber-50 rounded-xl">
              <Key className="text-amber-600" size={24} />
            </div>
            <div className="flex-1">
              <label className="block font-semibold text-gray-800 mb-1">
                Access Token
              </label>
              <p className="text-sm text-gray-500 mb-3">
                WhatsApp Cloud API access token. Keep this secret.
              </p>
              <div className="relative">
                <input
                  type={showToken ? 'text' : 'password'}
                  value={settings.accessToken}
                  onChange={(e) => setSettings({ ...settings, accessToken: e.target.value })}
                  placeholder="EAAxxxxxxxx..."
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent font-mono text-sm"
                  data-testid="whatsapp-access-token-input"
                />
                <button
                  type="button"
                  onClick={() => setShowToken(!showToken)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showToken ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {settings.accessToken && settings.accessToken.startsWith('***') && (
                <p className="text-xs text-gray-400 mt-2">
                  Token is masked for security. Enter a new token to update.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Phone Number ID */}
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-50 rounded-xl">
              <Phone className="text-blue-600" size={24} />
            </div>
            <div className="flex-1">
              <label className="block font-semibold text-gray-800 mb-1">
                Phone Number ID
              </label>
              <p className="text-sm text-gray-500 mb-3">
                The ID of your WhatsApp Business phone number
              </p>
              <input
                type="text"
                value={settings.phoneNumberId}
                onChange={(e) => setSettings({ ...settings, phoneNumberId: e.target.value })}
                placeholder="1234567890123456"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent font-mono text-sm"
                data-testid="whatsapp-phone-number-id-input"
              />
            </div>
          </div>
        </div>

        {/* Business Account ID */}
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-purple-50 rounded-xl">
              <Building2 className="text-purple-600" size={24} />
            </div>
            <div className="flex-1">
              <label className="block font-semibold text-gray-800 mb-1">
                WhatsApp Business Account ID
              </label>
              <p className="text-sm text-gray-500 mb-3">
                Your WhatsApp Business Account identifier
              </p>
              <input
                type="text"
                value={settings.businessAccountId}
                onChange={(e) => setSettings({ ...settings, businessAccountId: e.target.value })}
                placeholder="1234567890123456"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent font-mono text-sm"
                data-testid="whatsapp-business-account-id-input"
              />
            </div>
          </div>
        </div>

        {/* Webhook Verify Token */}
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-green-50 rounded-xl">
              <Shield className="text-green-600" size={24} />
            </div>
            <div className="flex-1">
              <label className="block font-semibold text-gray-800 mb-1">
                Webhook Verify Token
              </label>
              <p className="text-sm text-gray-500 mb-3">
                Token used to verify webhook requests from Meta
              </p>
              <input
                type="text"
                value={settings.webhookVerifyToken}
                onChange={(e) => setSettings({ ...settings, webhookVerifyToken: e.target.value })}
                placeholder="your_verify_token"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent font-mono text-sm"
                data-testid="whatsapp-webhook-verify-token-input"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Webhook URL Info */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-gray-100 rounded-xl">
            <Webhook className="text-gray-600" size={24} />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-800 mb-1">Webhook Configuration</h3>
            <p className="text-sm text-gray-500 mb-3">
              Configure this URL in your Meta Developer Console to receive incoming messages:
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-mono break-all">
                {webhookUrl}
              </code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(webhookUrl);
                  setMessage({ type: 'success', text: 'Webhook URL copied to clipboard!' });
                  setTimeout(() => setMessage(null), 3000);
                }}
                className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium"
              >
                Copy
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Use the Webhook Verify Token above when configuring in Meta Developer Console
            </p>
          </div>
        </div>
      </div>

      {/* Connection Validation Panel — Phase 12.2 */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="wa-validation-panel">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <ShieldCheck className="text-teal-600" size={18} />
          <h3 className="font-semibold text-gray-800">Connection Validation</h3>
          <span className="text-xs text-gray-400 ml-auto">Test against Meta v22.0 — bypasses workflows</span>
        </div>
        <div className="p-6 space-y-5">
          {/* Verify */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pb-5 border-b border-gray-100">
            <div className="md:max-w-xl">
              <p className="font-medium text-gray-800 text-sm">Verify WhatsApp Credentials</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Calls <code className="bg-gray-50 px-1.5 py-0.5 rounded">GET https://graph.facebook.com/v22.0/{'{phoneNumberId}'}</code> with the saved access token and shows the raw Meta response (invalid token / invalid phoneNumberId / permission / success).
              </p>
            </div>
            <button
              onClick={verifyCredentials}
              disabled={verifying}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 text-sm font-medium shrink-0"
              data-testid="wa-verify-btn"
            >
              {verifying ? <RefreshCw size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
              {verifying ? 'Verifying…' : 'Verify Credentials'}
            </button>
          </div>
          {verifyResult && (
            <div
              className={`rounded-lg border p-4 ${verifyResult.ok ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}
              data-testid="wa-verify-result"
            >
              <div className="flex items-start gap-3">
                {verifyResult.ok
                  ? <CheckCircle size={18} className="text-emerald-600 mt-0.5" />
                  : <AlertCircle size={18} className="text-rose-600 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold ${verifyResult.ok ? 'text-emerald-800' : 'text-rose-800'}`}>
                    {verifyResult.ok ? 'Credentials valid' : `Verification failed — ${verifyResult.category || 'unknown'}`}
                  </p>
                  <p className="text-xs text-gray-700 mt-1">{verifyResult.error || verifyResult.message}</p>
                  {verifyResult.ok && (
                    <p className="text-xs text-gray-700 mt-1">
                      <span className="font-medium">Display number:</span> {verifyResult.display_phone_number || '—'} &nbsp;|&nbsp;
                      <span className="font-medium">Verified name:</span> {verifyResult.verified_name || '—'}
                    </p>
                  )}
                  <div className="mt-2 text-[11px] text-gray-500 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 font-mono">
                    <span>HTTP: <span className="text-gray-800">{verifyResult.meta_status_code ?? '—'}</span></span>
                    <span>code: <span className="text-gray-800">{verifyResult.error_code ?? '—'}</span></span>
                    <span>subcode: <span className="text-gray-800">{verifyResult.error_subcode ?? '—'}</span></span>
                    <span>fbtrace: <span className="text-gray-800 truncate">{verifyResult.fbtrace_id ?? '—'}</span></span>
                  </div>
                  <details className="mt-2">
                    <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">View raw Meta response</summary>
                    <pre className="mt-2 text-[11px] bg-white border border-gray-200 rounded-md p-3 overflow-auto max-h-48" data-testid="wa-verify-raw">
{JSON.stringify(verifyResult.meta_response, null, 2)}
                    </pre>
                  </details>
                </div>
              </div>
            </div>
          )}

          {/* Send Test */}
          <div className="space-y-3" data-testid="wa-send-test-section">
            <div>
              <p className="font-medium text-gray-800 text-sm">Send Test WhatsApp</p>
              <p className="text-xs text-gray-500 mt-0.5">Bypasses workflows and sends directly via Meta Cloud API. Use the digits-only format <code className="bg-gray-50 px-1.5 py-0.5 rounded">91XXXXXXXXXX</code> (no + sign).</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">Recipient WA number</label>
                <input
                  type="text"
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  placeholder="91XXXXXXXXXX"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-teal-500 outline-none"
                  data-testid="wa-test-to-input"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">Message type</label>
                <select
                  value={testType}
                  onChange={(e) => setTestType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-teal-500 outline-none"
                  data-testid="wa-test-type-select"
                >
                  <option value="text">text</option>
                  <option value="template">template</option>
                  <option value="media">media</option>
                </select>
              </div>
            </div>

            {/* Conditional fields per type */}
            {testType === 'text' && (
              <div data-testid="wa-test-text-block">
                <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">Body</label>
                <input
                  type="text"
                  value={testText}
                  onChange={(e) => setTestText(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                  data-testid="wa-test-text-input"
                />
                <p className="text-[11px] text-amber-700 mt-1">Text messages are only delivered inside an active 24-hour customer-service window. Outside that, Meta returns HTTP 200 + a wamid but the message is NOT delivered to the device. Use the <code className="bg-gray-50 px-1 rounded">template</code> type for cold sends.</p>
              </div>
            )}
            {testType === 'template' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="wa-test-template-block">
                <div>
                  <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">Template name</label>
                  <input
                    type="text"
                    value={testTemplateName}
                    onChange={(e) => setTestTemplateName(e.target.value)}
                    placeholder="hello_world"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-teal-500 outline-none"
                    data-testid="wa-test-template-name"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">Language code</label>
                  <input
                    type="text"
                    value={testTemplateLang}
                    onChange={(e) => setTestTemplateLang(e.target.value)}
                    placeholder="en_US"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-teal-500 outline-none"
                    data-testid="wa-test-template-lang"
                  />
                </div>
              </div>
            )}
            {testType === 'media' && (
              <div className="space-y-3" data-testid="wa-test-media-block">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-2">
                    <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">Public media URL</label>
                    <input
                      type="url"
                      value={testMediaUrl}
                      onChange={(e) => setTestMediaUrl(e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-teal-500 outline-none"
                      data-testid="wa-test-media-url"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">Media type</label>
                    <select
                      value={testMediaType}
                      onChange={(e) => setTestMediaType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-teal-500 outline-none"
                      data-testid="wa-test-media-type"
                    >
                      <option value="image">image</option>
                      <option value="video">video</option>
                      <option value="audio">audio</option>
                      <option value="document">document</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">Caption (optional)</label>
                  <input
                    type="text"
                    value={testMediaCaption}
                    onChange={(e) => setTestMediaCaption(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                    data-testid="wa-test-media-caption"
                  />
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 pt-1">
              <button
                onClick={sendTest}
                disabled={testing || sendingMetaTest || !integrationEnabled}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                data-testid="wa-send-test-btn"
              >
                {testing ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
                {testing ? 'Sending…' : 'Send Test WhatsApp'}
              </button>
              {/* Phase 13.1 — exact Meta Dashboard payload (template hello_world / en_US) */}
              <button
                onClick={sendMetaTestPayload}
                disabled={testing || sendingMetaTest || !integrationEnabled}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                data-testid="wa-send-meta-test-btn"
                title="Sends the exact payload generated by Meta Dashboard's 'Send message' button: template hello_world / en_US."
              >
                {sendingMetaTest ? <RefreshCw size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
                {sendingMetaTest ? 'Sending Meta payload…' : 'Send Meta Test Payload'}
              </button>
            </div>
            {!integrationEnabled && (
              <p className="text-xs text-red-600 mt-1" data-testid="wa-test-disabled-note">
                Test sending is disabled while the WhatsApp Integration is turned off.
              </p>
            )}
          </div>
          {testResult && (
            <div
              className={`rounded-lg border p-4 ${testResult.success ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}
              data-testid="wa-test-result"
            >
              <div className="flex items-start gap-3">
                {testResult.success
                  ? <CheckCircle size={18} className="text-emerald-600 mt-0.5" />
                  : <AlertCircle size={18} className="text-rose-600 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold ${testResult.success ? 'text-emerald-800' : 'text-rose-800'}`}>
                    {testResult.success ? `Sent — wamid: ${testResult.message_id}` : 'Send failed'}
                  </p>
                  {!testResult.success && (
                    <p className="text-xs text-gray-700 mt-1">{testResult.error}</p>
                  )}
                  <div className="mt-2 text-[11px] text-gray-500 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 font-mono">
                    <span>HTTP: <span className="text-gray-800">{testResult.meta_status_code ?? '—'}</span></span>
                    <span>code: <span className="text-gray-800">{testResult.error_code ?? '—'}</span></span>
                    <span>subcode: <span className="text-gray-800">{testResult.error_subcode ?? '—'}</span></span>
                    <span>fbtrace: <span className="text-gray-800 truncate">{testResult.fbtrace_id ?? '—'}</span></span>
                  </div>
                  <details className="mt-2">
                    <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">View raw Meta response</summary>
                    <pre className="mt-2 text-[11px] bg-white border border-gray-200 rounded-md p-3 overflow-auto max-h-48" data-testid="wa-test-raw">
{JSON.stringify(testResult.meta_response, null, 2)}
                    </pre>
                  </details>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium shadow-sm"
          data-testid="save-whatsapp-settings-btn"
        >
          {saving ? (
            <>
              <RefreshCw className="animate-spin" size={18} />
              Saving...
            </>
          ) : (
            <>
              <Save size={18} />
              Save Settings
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default WhatsAppSettings;
